import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Paper, Typography, CircularProgress, Tooltip } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const THUMBNAIL_WIDTH = 100;
const THUMBNAIL_GAP = 8;

const ImageSlideshow = ({ images, maxHeight = 360, enableKeyboard = true }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoading, setImageLoading] = useState(true);
  const thumbnailContainerRef = useRef(null);

  useEffect(() => {
    setCurrentIndex(0);
    setImageLoading(true);
  }, [images]);

  useEffect(() => {
    if (!enableKeyboard || images.length <= 1) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setImageLoading(true);
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
      } else if (e.key === 'ArrowRight') {
        setImageLoading(true);
        setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enableKeyboard, images.length]);

  useEffect(() => {
    if (!thumbnailContainerRef.current) return;

    const container = thumbnailContainerRef.current;
    const scrollPosition =
      currentIndex * (THUMBNAIL_WIDTH + THUMBNAIL_GAP) -
      container.clientWidth / 2 +
      THUMBNAIL_WIDTH / 2;

    container.scrollTo({
      left: Math.max(0, scrollPosition),
      behavior: 'smooth'
    });
  }, [currentIndex]);

  if (!images?.length) return null;

  const current = images[currentIndex];
  const hasMultiple = images.length > 1;

  const goPrevious = () => {
    setImageLoading(true);
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const goNext = () => {
    setImageLoading(true);
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          bgcolor: 'action.hover',
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        {hasMultiple && (
          <Tooltip title="Previous (Left Arrow)">
            <IconButton
              onClick={goPrevious}
              sx={{
                position: 'absolute',
                left: 8,
                zIndex: 2,
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper' }
              }}
              size="small"
            >
              <NavigateBeforeIcon />
            </IconButton>
          </Tooltip>
        )}

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 2,
            minHeight: 200
          }}
        >
          {imageLoading && (
            <CircularProgress size={28} sx={{ position: 'absolute' }} />
          )}
          <Box
            component="img"
            src={current.src}
            alt={current.alt || `Image ${currentIndex + 1}`}
            onLoad={() => setImageLoading(false)}
            onError={(e) => {
              e.target.onerror = null;
              setImageLoading(false);
            }}
            sx={{
              maxWidth: '100%',
              maxHeight,
              objectFit: 'contain',
              display: imageLoading ? 'none' : 'block'
            }}
          />
        </Box>

        {hasMultiple && (
          <Tooltip title="Next (Right Arrow)">
            <IconButton
              onClick={goNext}
              sx={{
                position: 'absolute',
                right: 8,
                zIndex: 2,
                bgcolor: 'background.paper',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.paper' }
              }}
              size="small"
            >
              <NavigateNextIcon />
            </IconButton>
          </Tooltip>
        )}

        {hasMultiple && (
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 12,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: 'common.white',
              px: 1,
              py: 0.25,
              borderRadius: 1
            }}
          >
            {currentIndex + 1} / {images.length}
          </Typography>
        )}
      </Box>

      {hasMultiple && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
            All images ({images.length})
          </Typography>
          <Box
            ref={thumbnailContainerRef}
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              pb: 1,
              '&::-webkit-scrollbar': { height: '6px' },
              '&::-webkit-scrollbar-track': { backgroundColor: 'action.hover' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'text.disabled',
                borderRadius: '3px'
              }
            }}
          >
            {images.map((image, index) => (
              <Paper
                key={`${image.src}-${index}`}
                elevation={currentIndex === index ? 4 : 0}
                onClick={() => {
                  setImageLoading(true);
                  setCurrentIndex(index);
                }}
                sx={{
                  cursor: 'pointer',
                  p: 0.5,
                  borderRadius: 1,
                  border: '2px solid',
                  borderColor: currentIndex === index ? 'primary.main' : 'transparent',
                  minWidth: THUMBNAIL_WIDTH,
                  maxWidth: THUMBNAIL_WIDTH,
                  bgcolor: currentIndex === index ? 'action.selected' : 'background.paper',
                  flexShrink: 0,
                  transition: 'border-color 0.2s, box-shadow 0.2s'
                }}
              >
                <Typography variant="caption" noWrap sx={{ display: 'block', textAlign: 'center', mb: 0.5 }}>
                  #{index + 1}
                </Typography>
                <Box
                  sx={{
                    height: 64,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    overflow: 'hidden',
                    borderRadius: 0.5
                  }}
                >
                  <Box
                    component="img"
                    src={image.src}
                    alt={image.alt || `Thumbnail ${index + 1}`}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ImageSlideshow;
